import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';
import { contractorService } from '../../../services/contractorService';
import { projectService } from '../../../services/projectService';
import EditContractorModal from './EditContractorModal';
import TeamMemberSelector from '../../../components/ui/TeamMemberSelector';
import PermissionDenied from '../../../components/ui/PermissionDenied';
import { usePermissions } from '../../../hooks/usePermissions';

const ContractorProfile = ({ contractor, onClose, onUpdate }) => {
  const permissions = usePermissions();
  const [activeTab, setActiveTab] = useState('overview');
  const [availability, setAvailability] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTeamMemberModal, setShowTeamMemberModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [editingAvailability, setEditingAvailability] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState(contractor?.availabilityStatus || 'available');
  const [updatingAvailability, setUpdatingAvailability] = useState(false);
  const [permissionError, setPermissionError] = useState('');
  const [liveMetrics, setLiveMetrics] = useState(null);

  // Assign to Project modal state
  const [showAssignProjectModal, setShowAssignProjectModal] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [assignProjectForm, setAssignProjectForm] = useState({
    projectId: '',
    role: '',
    startDate: new Date()?.toISOString()?.split('T')?.[0],
    endDate: '',
    status: 'assigned',
    notes: ''
  });
  const [assigningProject, setAssigningProject] = useState(false);
  const [assignProjectError, setAssignProjectError] = useState('');

  useEffect(() => {
    loadContractorDetails();
  }, [contractor?.id]);

  const handleEditSuccess = () => {
    setShowEditModal(false);
    onUpdate?.();
  };

  const handleAvailabilityChange = async (newStatus) => {
    setUpdatingAvailability(true);
    try {
      await contractorService?.update(contractor?.id, {
        availabilityStatus: newStatus
      });
      setAvailabilityStatus(newStatus);
      setEditingAvailability(false);
      onUpdate?.();
    } catch (error) {
      console.error('Error updating availability:', error);
      alert('Failed to update availability status');
    } finally {
      setUpdatingAvailability(false);
    }
  };

  const handleNavigateToProject = (projectId) => {
    const event = new CustomEvent('openProjectDetails', {
      detail: { projectId }
    });
    window.dispatchEvent(event);
    onClose?.();
  };

  const handleNavigateToSettings = (tab = 'team') => {
    const event = new CustomEvent('navigateToSettings', {
      detail: { tab }
    });
    window.dispatchEvent(event);
    onClose?.();
  };

  const handleEditClick = () => {
    if (!permissions?.canEdit) {
      setPermissionError('You do not have permission to edit contractors.');
      setTimeout(() => setPermissionError(''), 4000);
      return;
    }
    setShowEditModal(true);
  };

  const handleAvailabilityEditClick = () => {
    if (!permissions?.canEdit) {
      setPermissionError('You do not have permission to update availability.');
      setTimeout(() => setPermissionError(''), 4000);
      return;
    }
    setEditingAvailability(true);
  };

  const handleLinkTeamMember = (assignmentId) => {
    if (!permissions?.canManageTeam) {
      setPermissionError('You do not have permission to link team members.');
      setTimeout(() => setPermissionError(''), 4000);
      return;
    }
    setSelectedAssignment(assignmentId);
    setShowTeamMemberModal(true);
  };

  const handleTeamMemberSelect = async (member) => {
    if (!selectedAssignment) return;
    
    try {
      const { error } = await supabase
        ?.from('project_assignments')
        ?.update({ team_member_id: member?.id })
        ?.eq('id', selectedAssignment);

      if (error) throw error;
      
      await loadContractorDetails();
      setShowTeamMemberModal(false);
      setSelectedAssignment(null);
    } catch (error) {
      console.error('Error linking team member:', error);
      alert('Failed to link team member');
    }
  };

  const handleOpenAssignProject = async () => {
    if (!permissions?.canEdit) {
      setPermissionError('You do not have permission to assign projects.');
      setTimeout(() => setPermissionError(''), 4000);
      return;
    }
    setAssignProjectError('');
    setAssignProjectForm({
      projectId: '',
      role: contractor?.role || '',
      startDate: new Date()?.toISOString()?.split('T')?.[0],
      endDate: '',
      status: 'assigned',
      notes: ''
    });
    setShowAssignProjectModal(true);
    setLoadingProjects(true);
    try {
      const data = await projectService?.getAll();
      setProjects(data || []);
    } catch (err) {
      console.error('Error loading projects:', err);
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleAssignProjectSubmit = async (e) => {
    e?.preventDefault();
    if (!assignProjectForm?.projectId) {
      setAssignProjectError('Please select a project');
      return;
    }
    setAssigningProject(true);
    setAssignProjectError('');
    try {
      await projectService?.assignContractor(assignProjectForm?.projectId, contractor?.id, {
        role: assignProjectForm?.role,
        startDate: assignProjectForm?.startDate,
        endDate: assignProjectForm?.endDate || null,
        status: assignProjectForm?.status,
        notes: assignProjectForm?.notes
      });
      setShowAssignProjectModal(false);
      await loadContractorDetails();
      onUpdate?.();
    } catch (err) {
      console.error('Error assigning project:', err);
      setAssignProjectError(err?.message || 'Failed to assign project');
    } finally {
      setAssigningProject(false);
    }
  };

  const loadContractorDetails = async () => {
    setLoading(true);
    try {
      // Load availability calendar
      const { data: availData } = await supabase
        ?.from('contractor_availability')
        ?.select('*')
        ?.eq('contractor_id', contractor?.id)
        ?.order('date', { ascending: true })
        ?.limit(14);
      setAvailability(availData || []);

      // Load project assignments
      const { data: assignData } = await supabase
        ?.from('project_assignments')
        ?.select('*, projects(customer_name, address, phase)')
        ?.eq('contractor_id', contractor?.id)
        ?.order('created_at', { ascending: false });
      setAssignments(assignData || []);

      // Load performance history
      const { data: perfData } = await supabase
        ?.from('contractor_performance')
        ?.select('*, projects(customer_name, address)')
        ?.eq('contractor_id', contractor?.id)
        ?.order('completed_date', { ascending: false })
        ?.limit(5);
      setPerformance(perfData || []);
    } catch (error) {
      console.error('Error loading contractor details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAvailabilityBadge = (status) => {
    const badges = {
      available: { label: 'Available', className: 'bg-success/10 text-success', icon: 'CheckCircle' },
      busy: { label: 'Busy', className: 'bg-warning/10 text-warning', icon: 'Clock' },
      on_leave: { label: 'On Leave', className: 'bg-muted text-muted-foreground', icon: 'Calendar' },
      unavailable: { label: 'Unavailable', className: 'bg-destructive/10 text-destructive', icon: 'XCircle' }
    };
    return badges?.[status] || badges?.available;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const availabilityBadge = getAvailabilityBadge(availabilityStatus);
  const rating = contractor?.rating || 0;
  const completionRate = contractor?.completionRate || 0;
  const qualityScore = contractor?.qualityScore || 0;
  const timelineAdherence = contractor?.timelineAdherence || 0;
  const customerSatisfaction = contractor?.customerSatisfaction || 0;

  const renderAssignments = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Project Assignments</h3>
        <Button
          onClick={handleOpenAssignProject}
          leftIcon={<Icon name="Plus" size={16} />}
          size="sm"
        >
          Assign to Project
        </Button>
      </div>
      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Loading assignments...</div>
      ) : assignments?.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="Briefcase" size={48} className="mx-auto mb-3 opacity-50" />
          <p>No project assignments found</p>
        </div>
      ) : (
        assignments?.map((assignment) => (
          <button
            key={assignment?.id}
            onClick={() => handleNavigateToProject(assignment?.project_id)}
            className="w-full bg-muted/50 rounded-lg p-4 hover:bg-muted transition-colors text-left"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex-1">
                <h4 className="font-semibold text-foreground mb-1">
                  {assignment?.projects?.customer_name || 'Unknown Customer'}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {assignment?.projects?.address}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Role: {assignment?.role || 'Contractor'}
                </p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                  assignment?.status === 'in_progress' ? 'bg-warning/10 text-warning' :
                  assignment?.status === 'completed' ? 'bg-success/10 text-success' :
                  assignment?.status === 'assigned'? 'bg-blue-100 text-blue-700' : 'bg-muted text-muted-foreground'
                }`}>
                  {assignment?.status?.replace('_', ' ')?.charAt(0)?.toUpperCase() + assignment?.status?.slice(1)?.replace('_', ' ')}
                </span>
                <span className="text-xs text-primary flex items-center gap-1">
                  <Icon name="ExternalLink" size={12} />
                  View Project
                </span>
                {!assignment?.team_member_id && (
                  <button
                    onClick={(e) => {
                      e?.stopPropagation();
                      handleLinkTeamMember(assignment?.id);
                    }}
                    className="text-primary hover:text-primary/80 text-xs flex items-center gap-1"
                  >
                    <Icon name="UserPlus" size={12} />
                    Link Team Member
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Start Date</p>
                <p className="font-medium text-foreground">{formatDate(assignment?.start_date)}</p>
              </div>
              {assignment?.end_date && (
                <div>
                  <p className="text-xs text-muted-foreground">End Date</p>
                  <p className="font-medium text-foreground">{formatDate(assignment?.end_date)}</p>
                </div>
              )}
            </div>
            {assignment?.team_member_id && (
              <div className="mt-3 pt-3 border-t border-border">
                <p className="text-xs text-muted-foreground mb-1">Coordinating Team Member</p>
                <button
                  onClick={(e) => {
                    e?.stopPropagation();
                    const navEvent = new CustomEvent('navigateToSettings', {
                      detail: { tab: 'team' }
                    });
                    window.dispatchEvent(navEvent);
                  }}
                  className="text-sm text-primary hover:underline flex items-center gap-1"
                >
                  View Team Member
                  <Icon name="ExternalLink" size={12} />
                </button>
              </div>
            )}
            {assignment?.notes && (
              <div className="mt-3 pt-3 border-t border-border">
                <p className="text-xs text-muted-foreground">{assignment?.notes}</p>
              </div>
            )}
          </button>
        ))
      )}
    </div>
  );

  const renderPerformance = () => (
    <div>
      <h3 className="text-lg font-semibold text-foreground mb-4">Performance History</h3>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading performance history...</p>
      ) : performance?.length === 0 ? (
        <p className="text-sm text-muted-foreground">No performance history found.</p>
      ) : (
        <div className="space-y-4">
          {performance?.map((perf) => (
            <div key={perf?.id} className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-semibold text-foreground">{perf?.projects?.customer_name}</p>
                  <p className="text-sm text-muted-foreground">{perf?.projects?.address}</p>
                  <p className="text-xs text-muted-foreground mt-1">Completed: {formatDate(perf?.completed_date)}</p>
                </div>
                <div className="flex items-center gap-1">
                  <Icon name="Star" size={16} className="text-warning fill-warning" />
                  <span className="font-semibold text-foreground">{perf?.rating?.toFixed(1)}</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div>
                  <p className="text-xs text-muted-foreground">Quality</p>
                  <p className="text-sm font-medium text-foreground">{perf?.quality_score?.toFixed(1)}/5</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Timeline</p>
                  <p className="text-sm font-medium text-foreground">{perf?.timeline_adherence?.toFixed(1)}%</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Overall</p>
                  <p className="text-sm font-medium text-foreground">{perf?.rating?.toFixed(1)}/5</p>
                </div>
              </div>
              {perf?.customer_feedback && (
                <div className="p-3 bg-card rounded-lg border border-border">
                  <p className="text-xs text-muted-foreground mb-1">Customer Feedback</p>
                  <p className="text-sm text-foreground italic">"{perf?.customer_feedback}"</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={onClose}>
      <div
        className="bg-card rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-4xl flex flex-col"
        style={{ maxHeight: '92vh', paddingBottom: 'max(0px, env(safe-area-inset-bottom, 0px))' }}
        onClick={(e) => e?.stopPropagation()}
      >
        {/* Drag handle on mobile */}
        <div className="w-10 h-1 bg-white/30 rounded-full mx-auto mt-3 mb-0 sm:hidden flex-shrink-0" />

        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-accent p-4 sm:p-6 text-white flex-shrink-0">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                {contractor?.avatar ? (
                  <img src={contractor?.avatar} alt={contractor?.name} className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover" />
                ) : (
                  <Icon name="User" size={28} className="text-white" />
                )}
              </div>
              <div className="min-w-0">
                <h2 className="text-lg sm:text-2xl font-bold truncate">{contractor?.name}</h2>
                <p className="text-white/90 text-sm truncate">{contractor?.role}</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {permissions?.canEdit && (
                <button onClick={handleEditClick} className="p-2 hover:bg-white/20 rounded-lg transition-colors min-w-[40px] min-h-[40px] flex items-center justify-center">
                  <Icon name="Edit" size={18} className="text-white" />
                </button>
              )}
              <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors min-w-[40px] min-h-[40px] flex items-center justify-center">
                <Icon name="X" size={22} className="text-white" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {editingAvailability ? (
              <div className="flex items-center gap-2">
                <select
                  value={availabilityStatus}
                  onChange={(e) => setAvailabilityStatus(e?.target?.value)}
                  disabled={updatingAvailability}
                  className="px-3 py-1 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="available">Available</option>
                  <option value="busy">Busy</option>
                  <option value="on_leave">On Leave</option>
                  <option value="unavailable">Unavailable</option>
                </select>
                <Button size="sm" onClick={() => handleAvailabilityChange(availabilityStatus)} disabled={updatingAvailability}>
                  {updatingAvailability ? <Icon name="Loader2" size={14} className="animate-spin" /> : <Icon name="Check" size={14} />}
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setEditingAvailability(false); setAvailabilityStatus(contractor?.availabilityStatus || 'available'); }} disabled={updatingAvailability}>
                  <Icon name="X" size={14} />
                </Button>
              </div>
            ) : (
              <button
                onClick={handleAvailabilityEditClick}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-white/20 hover:bg-white/30 transition-colors"
              >
                <Icon name={availabilityBadge?.icon} size={16} />
                {availabilityBadge?.label}
                {permissions?.canEdit && <Icon name="Edit" size={12} className="ml-1" />}
              </button>
            )}
            <div className="flex items-center gap-1">
              <Icon name="Star" size={18} className="text-warning fill-warning" />
              <span className="font-semibold">{rating?.toFixed(1)}</span>
            </div>
          </div>
        </div>

        {/* Tabs - horizontally scrollable on mobile */}
        <div className="border-b border-border flex-shrink-0">
          <div className="tabs-scroll-container px-4 sm:px-6 gap-0">
            {['overview', 'availability', 'assignments', 'performance']?.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium capitalize transition-colors border-b-2 whitespace-nowrap flex-shrink-0 min-h-[44px] ${
                  activeTab === tab
                    ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Content - scrollable */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
          {/* Permission Denied Banner */}
          {permissionError && (
            <div className="mb-4">
              <PermissionDenied message={permissionError} />
            </div>
          )}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Contact Information */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon name="Mail" size={20} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Email</p>
                      <p className="text-sm font-medium text-foreground">{contractor?.email}</p>
                    </div>
                  </div>
                  {contractor?.phone && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon name="Phone" size={20} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Phone</p>
                        <p className="text-sm font-medium text-foreground">{contractor?.phone}</p>
                      </div>
                    </div>
                  )}
                  {contractor?.location && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon name="MapPin" size={20} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Location</p>
                        <p className="text-sm font-medium text-foreground">{contractor?.location}</p>
                      </div>
                    </div>
                  )}
                  {contractor?.hourlyRate && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon name="DollarSign" size={20} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Hourly Rate</p>
                        <p className="text-sm font-medium text-foreground">${contractor?.hourlyRate}/hr</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Performance Metrics */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">Performance Metrics</h3>
                {/* Live metrics from project_assignments */}
                {liveMetrics && (
                  <div className="mb-3 p-3 bg-primary/5 border border-primary/20 rounded-lg flex flex-wrap gap-4 text-sm">
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      <Icon name="Briefcase" size={14} className="text-primary" />
                      <span className="font-medium text-foreground">{liveMetrics?.totalAssignments}</span> total bookings
                    </span>
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      <Icon name="Activity" size={14} className="text-warning" />
                      <span className="font-medium text-foreground">{liveMetrics?.activeCount}</span> active
                    </span>
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      <Icon name="CheckCircle" size={14} className="text-success" />
                      <span className="font-medium text-foreground">{liveMetrics?.completedCount}</span> completed
                    </span>
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      <Icon name="TrendingUp" size={14} className="text-accent" />
                      <span className="font-medium text-foreground">{liveMetrics?.computedCompletionRate}%</span> completion rate (live)
                    </span>
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-muted/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="CheckCircle" size={16} className="text-success" />
                      <p className="text-xs text-muted-foreground">Completion Rate</p>
                    </div>
                    <p className="text-2xl font-bold text-foreground">
                      {liveMetrics ? `${liveMetrics?.computedCompletionRate}%` : `${completionRate?.toFixed(1)}%`}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {liveMetrics ? 'from assignments' : 'profile data'}
                    </p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Award" size={16} className="text-primary" />
                      <p className="text-xs text-muted-foreground">Quality Score</p>
                    </div>
                    <p className="text-2xl font-bold text-foreground">{qualityScore?.toFixed(1)}/5</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Clock" size={16} className="text-warning" />
                      <p className="text-xs text-muted-foreground">Timeline</p>
                    </div>
                    <p className="text-2xl font-bold text-foreground">{timelineAdherence?.toFixed(1)}%</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="ThumbsUp" size={16} className="text-accent" />
                      <p className="text-xs text-muted-foreground">Satisfaction</p>
                    </div>
                    <p className="text-2xl font-bold text-foreground">{customerSatisfaction?.toFixed(1)}/5</p>
                  </div>
                </div>
              </div>

              {/* Certifications */}
              {contractor?.certifications && contractor?.certifications?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">Certifications</h3>
                  <div className="flex flex-wrap gap-2">
                    {contractor?.certifications?.map((cert, index) => (
                      <span key={index} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium">
                        <Icon name="Award" size={14} />
                        {cert}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Experience */}
              {contractor?.yearsExperience && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">Experience</h3>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center">
                      <Icon name="Briefcase" size={24} className="text-accent" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">{contractor?.yearsExperience} years</p>
                      <p className="text-sm text-muted-foreground">{contractor?.totalProjects || 0} total projects • {contractor?.completedProjects || 0} completed</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'availability' && (
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">Availability Calendar (Next 14 Days)</h3>
              {loading ? (
                <p className="text-sm text-muted-foreground">Loading availability...</p>
              ) : availability?.length === 0 ? (
                <p className="text-sm text-muted-foreground">No availability data found.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {availability?.map((avail) => {
                    const badge = getAvailabilityBadge(avail?.status);
                    return (
                      <div key={avail?.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div>
                          <p className="font-medium text-foreground">{formatDate(avail?.date)}</p>
                          {avail?.notes && <p className="text-xs text-muted-foreground mt-1">{avail?.notes}</p>}
                        </div>
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${badge?.className}`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                          {badge?.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'assignments' && renderAssignments()}

          {activeTab === 'performance' && renderPerformance()}
        </div>

        {/* Footer */}
        <div className="border-t border-border p-4 flex justify-end gap-3 flex-shrink-0">
          <Button variant="outline" onClick={onClose} className="min-h-[44px]">
            Close
          </Button>
        </div>
      </div>

      {showEditModal && (
        <EditContractorModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSuccess={handleEditSuccess}
          contractor={contractor}
        />
      )}

      {showTeamMemberModal && (
        <TeamMemberSelector
          isOpen={showTeamMemberModal}
          onClose={() => {
            setShowTeamMemberModal(false);
            setSelectedAssignment(null);
          }}
          onSelect={handleTeamMemberSelect}
          title="Link Team Member for Coordination"
          showAvailability={true}
        />
      )}

      {/* Assign to Project Modal */}
      {showAssignProjectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4" onClick={() => setShowAssignProjectModal(false)}>
          <div
            className="bg-card w-full sm:rounded-xl sm:max-w-md shadow-2xl rounded-t-2xl max-h-[92vh] flex flex-col"
            style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom, 16px))' }}
            onClick={(e) => e?.stopPropagation()}
          >
            <div className="w-10 h-1 bg-border rounded-full mx-auto mt-3 mb-1 sm:hidden flex-shrink-0" />
            <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
              <h2 className="text-lg font-semibold text-foreground">Assign to Project</h2>
              <button onClick={() => setShowAssignProjectModal(false)} className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground transition-colors">
                <Icon name="X" size={22} />
              </button>
            </div>
            <form onSubmit={handleAssignProjectSubmit} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {assignProjectError && (
                <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-lg text-sm">
                  {assignProjectError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Project <span className="text-destructive">*</span>
                </label>
                <Select
                  value={assignProjectForm?.projectId}
                  onChange={(val) => setAssignProjectForm(prev => ({ ...prev, projectId: val }))}
                  options={projects?.map(p => ({ value: p?.id, label: `${p?.customerName} - ${p?.address}` }))}
                  placeholder={loadingProjects ? 'Loading projects...' : 'Select project'}
                  disabled={loadingProjects}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Role</label>
                <Input
                  type="text"
                  value={assignProjectForm?.role}
                  onChange={(e) => setAssignProjectForm(prev => ({ ...prev, role: e?.target?.value }))}
                  placeholder="e.g., General Contractor, Electrician"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Start Date</label>
                  <Input
                    type="date"
                    value={assignProjectForm?.startDate}
                    onChange={(e) => setAssignProjectForm(prev => ({ ...prev, startDate: e?.target?.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">End Date</label>
                  <Input
                    type="date"
                    value={assignProjectForm?.endDate}
                    onChange={(e) => setAssignProjectForm(prev => ({ ...prev, endDate: e?.target?.value }))}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Status</label>
                <Select
                  value={assignProjectForm?.status}
                  onChange={(val) => setAssignProjectForm(prev => ({ ...prev, status: val }))}
                  options={[
                    { value: 'assigned', label: 'Assigned' },
                    { value: 'in_progress', label: 'In Progress' },
                    { value: 'completed', label: 'Completed' },
                    { value: 'on_hold', label: 'On Hold' }
                  ]}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Notes</label>
                <textarea
                  value={assignProjectForm?.notes}
                  onChange={(e) => setAssignProjectForm(prev => ({ ...prev, notes: e?.target?.value }))}
                  rows={3}
                  className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary text-sm resize-none"
                  placeholder="Additional notes about this assignment"
                />
              </div>
            </form>
            <div className="flex gap-3 px-5 pt-3 border-t border-border flex-shrink-0">
              <Button type="button" variant="outline" onClick={() => setShowAssignProjectModal(false)} className="flex-1 h-12" disabled={assigningProject}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1 h-12" disabled={assigningProject || loadingProjects} onClick={handleAssignProjectSubmit}>
                {assigningProject ? (
                  <span className="flex items-center gap-2">
                    <Icon name="Loader2" size={16} className="animate-spin" />
                    Assigning...
                  </span>
                ) : 'Assign to Project'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractorProfile;